import React, { useState } from 'react';
import { Field } from '../../../components/technician/ui/Field';
import { KEY_STATE, KEY_STATES } from '../catalog/keyState';
import type { KeyState } from '../catalog/keyState';
import { KEYPAD_LIMITS } from '../catalog/limits';
import { keyDisplayName, keyNumber, layoutOf } from '../model';
import type { Keypad, KeypadKey } from '../model';
import { KEY_LAYOUT_SPECS } from '../catalog/layout';
import { reportFor } from '../stored';
import type { KeyStateReport } from '../stored';
import { useKeypadStore } from '../store';
import type { MappingTarget } from '../targets';
import { StateEditorSheet } from './StateEditorSheet';
import { StateRow } from './StateRow';

export interface KeyEditorProps {
  readonly keypad: Keypad;
  /** Named `keypadKey`: `key` is React's own prop, and this is not it. */
  readonly keypadKey: KeypadKey;
  readonly reports: readonly KeyStateReport[];
  readonly targets: readonly MappingTarget[];
}

/**
 * One key: what it is called, and what its two states do.
 *
 * The name is the engraving label, which is why it is edited in place and why an
 * unnamed key is called by its position rather than by an empty field. The two
 * states are always both shown, in press-then-release order, so an empty release
 * is visible as a decision rather than as something nobody has got to yet.
 */
export const KeyEditor: React.FC<KeyEditorProps> = ({ keypad, keypadKey, reports, targets }) => {
  const [editing, setEditing] = useState<KeyState | null>(null);
  const renameKey = useKeypadStore((store) => store.renameKey);
  const clearState = useKeypadStore((store) => store.clearState);
  const mirrorAsOff = useKeypadStore((store) => store.mirrorAsOff);

  const layout = KEY_LAYOUT_SPECS[layoutOf(keypad)];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-lg text-[#333333]">
          {keyDisplayName(keypad, keypadKey)}
        </h3>
        <p className="text-sm text-[#666666]">
          Key {keyNumber(keypadKey)} on a {layout.label} panel.
        </p>
      </div>

      <Field
        label="Engraving"
        value={keypadKey.name}
        onChange={(name) => renameKey(keypad.id, keypadKey.id, name)}
        placeholder={keyDisplayName(keypad, keypadKey)}
        maxLength={KEYPAD_LIMITS.KEY_NAME_MAX}
        hint="What is printed on the key. Left empty, the console calls it by its number."
      />

      {KEY_STATES.map((state) => {
        const report = reportFor(reports, keypadKey.id, state);
        if (report === undefined) return null;
        return (
          <StateRow
            key={state}
            report={report}
            targets={targets}
            onEdit={setEditing}
            onClear={(target) => clearState(targets, keypad.id, keypadKey.id, target)}
            onMirror={(from) => mirrorAsOff(targets, keypad.id, keypadKey.id, from)}
          />
        );
      })}

      <StateEditorSheet
        isOpen={editing !== null}
        keypad={keypad}
        keypadKey={keypadKey}
        state={editing ?? KEY_STATE.PRESS}
        targets={targets}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};

export default KeyEditor;
